import { h } from '../dom'
import type { ComposerTarget } from '../state'
import { t } from '../i18n'

const WIDTH = 300
const GUTTER = 12

export interface ComposerHandlers {
  save: (comment: string) => void
  cancel: () => void
}

/**
 * Anchored note editor.
 *
 * Its draft text is held in the DOM rather than in the store on purpose:
 * re-rendering on every keystroke would fight the textarea for the caret.
 * Only Save reads the value back out.
 */
export function renderComposer(
  target: ComposerTarget,
  index: number,
  handlers: ComposerHandlers,
): HTMLElement {
  const textarea = h('textarea', {
    placeholder: t().composerPlaceholder,
    rows: '3',
    'aria-label': t().composerAria,
  }) as HTMLTextAreaElement

  const save = h('button', {
    class: 'primary',
    type: 'button',
    text: t().saveNote,
    onclick: () => handlers.save(textarea.value),
  }) as HTMLButtonElement
  save.disabled = true

  textarea.addEventListener('input', () => {
    save.disabled = textarea.value.trim() === ''
  })

  const node = h(
    'div',
    { class: 'composer', style: positionFor(target), role: 'dialog', 'aria-label': t().composerTitle },
    h(
      'div',
      { class: 'composer-head' },
      h('span', { class: 'dot', text: String(index) }),
      h('span', { class: 'title', text: t().composerTitle }),
      h('span', { class: 'chip', text: t().typeLabel[target.type] }),
    ),
    textarea,
    h(
      'div',
      { class: 'actions' },
      h('button', { class: 'secondary', type: 'button', text: t().cancel, onclick: handlers.cancel }),
      save,
    ),
  )

  // Focus after the node is in the document, otherwise the caret goes nowhere.
  queueMicrotask(() => textarea.focus())

  return node
}

/**
 * Places the composer just below its annotation, clamped to the *viewport* so
 * it is always reachable — the annotation may sit near a page edge, but the
 * editor must not.
 */
function positionFor(target: ComposerTarget): string {
  const minX = window.scrollX + GUTTER
  const maxX = window.scrollX + window.innerWidth - WIDTH - GUTTER
  const left = Math.max(minX, Math.min(target.anchorX, Math.max(minX, maxX)))
  return `left:${Math.round(left)}px;top:${Math.round(target.anchorY + 16)}px;`
}
